import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useDocumentosGDB, DocumentoGDB } from "@/hooks/useDocumentosGDB";

interface EditarMetadatosModalProps {
  documento: DocumentoGDB | null;
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIAS = [
  'Cartografía Base',
  'Estudios de Impacto Ambiental',
  'Planes de Manejo Ambiental',
  'Trámites ANLA',
  'Línea Base',
  'Área de Influencia',
  'Zonificación Ambiental',
  'Otros'
];

export const EditarMetadatosModal: React.FC<EditarMetadatosModalProps> = ({ documento, isOpen, onClose }) => {
  const { updateDocumento } = useDocumentosGDB();
  const [nombreDocumento, setNombreDocumento] = useState("");
  const [categoria, setCategoria] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [sistemaCoordenadas, setSistemaCoordenadas] = useState("");
  const [proyeccion, setProyeccion] = useState("");
  const [escala, setEscala] = useState("");
  const [fechaCaptura, setFechaCaptura] = useState<Date>();
  const [fuenteInformacion, setFuenteInformacion] = useState("");
  const [resolucionEspacial, setResolucionEspacial] = useState("");
  const [areaCobertura, setAreaCobertura] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (documento) {
      setNombreDocumento(documento.nombre_documento);
      setCategoria(documento.categoria);
      setDescripcion(documento.descripcion || "");
      setSistemaCoordenadas(documento.sistema_coordenadas || "");
      setProyeccion(documento.proyeccion || "");
      setEscala(documento.escala || "");
      setFechaCaptura(documento.fecha_captura ? new Date(documento.fecha_captura) : undefined);
      setFuenteInformacion(documento.fuente_informacion || "");
      setResolucionEspacial(documento.resolucion_espacial || "");
      setAreaCobertura(documento.area_cobertura || "");
    }
  }, [documento]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documento) return;

    setLoading(true);
    const success = await updateDocumento(documento.id, {
      nombre_documento: nombreDocumento,
      categoria,
      descripcion,
      sistema_coordenadas: sistemaCoordenadas,
      proyeccion,
      escala,
      fecha_captura: fechaCaptura?.toISOString(),
      fuente_informacion: fuenteInformacion,
      resolucion_espacial: resolucionEspacial,
      area_cobertura: areaCobertura
    });

    setLoading(false);
    if (success) {
      onClose();
    }
  };

  if (!documento) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Metadatos</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del Documento *</Label>
              <Input
                id="nombre"
                value={nombreDocumento}
                onChange={(e) => setNombreDocumento(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoria">Categoría *</Label>
              <Select value={categoria} onValueChange={setCategoria} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="font-semibold mb-4">Metadatos Geográficos</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sistema">Sistema de Coordenadas</Label>
                <Input
                  id="sistema"
                  value={sistemaCoordenadas}
                  onChange={(e) => setSistemaCoordenadas(e.target.value)}
                  placeholder="Ej: MAGNA-SIRGAS"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="proyeccion">Proyección</Label>
                <Input
                  id="proyeccion"
                  value={proyeccion}
                  onChange={(e) => setProyeccion(e.target.value)}
                  placeholder="Ej: EPSG:3116"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="escala">Escala</Label>
                <Input
                  id="escala"
                  value={escala}
                  onChange={(e) => setEscala(e.target.value)}
                  placeholder="Ej: 1:25,000"
                />
              </div>

              <div className="space-y-2">
                <Label>Fecha de Captura</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !fechaCaptura && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {fechaCaptura ? format(fechaCaptura, "PPP") : "Seleccionar fecha"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={fechaCaptura}
                      onSelect={setFechaCaptura}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fuente">Fuente de Información</Label>
                <Input
                  id="fuente"
                  value={fuenteInformacion}
                  onChange={(e) => setFuenteInformacion(e.target.value)}
                  placeholder="Ej: IGAC, IDEAM"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resolucion">Resolución Espacial</Label>
                <Input
                  id="resolucion"
                  value={resolucionEspacial}
                  onChange={(e) => setResolucionEspacial(e.target.value)}
                  placeholder="Ej: 30m/pixel"
                />
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <Label htmlFor="area">Área de Cobertura</Label>
              <Textarea
                id="area"
                value={areaCobertura}
                onChange={(e) => setAreaCobertura(e.target.value)}
                placeholder="Ej: Municipio de Bogotá, Cundinamarca"
                rows={2}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
